import React, { useEffect, useState } from 'react';
import { Lock, Users } from 'lucide-react';
import driveApi, { ROLES } from '../../api/drive';
import { useDrive } from '../../contexts/DriveProvider';
import { auditActor, auditLabel, formatDate, formatSize, initials, kindOf, previewType } from '../../lib/files';
import { FileTag } from './ItemsView';

const Thumb = ({ item }) => {
  const [url, setUrl] = useState(null);
  const isImage = previewType(item) === 'image';

  useEffect(() => {
    setUrl(null);
    if (!isImage) return;
    let cancelled = false;
    driveApi.downloadUrl([item.id], true).then((u) => !cancelled && setUrl(u)).catch(() => {});
    return () => { cancelled = true; };
  }, [item.id, isImage]);

  return (
    <div className="h-[148px] overflow-hidden rounded-lg border border-border bg-muted">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <FileTag item={item} size="lg" />}
    </div>
  );
};

/** Right-hand panel: the selected item (or the open folder) with its access and recent activity. */
const DetailsPanel = ({ item, onShare, selectionCount = 0 }) => {
  const { version } = useDrive();
  const [access, setAccess] = useState(null);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    setAccess(null);
    setEvents(null);
    if (!item) return;
    driveApi.access(item.id).then(setAccess).catch(() => setAccess(null));
    if (item.role >= ROLES.MANAGER) {
      driveApi.audit({ node: item.id }).then((list) => setEvents(list.slice(0, 4))).catch(() => setEvents(null));
    }
  }, [item?.id, item?.role, version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (selectionCount > 1) {
    return (
      <aside aria-label="Details" className="py-1">
        <b className="text-[15px] font-semibold">{selectionCount} items selected</b>
        <p className="mt-1 text-sm text-muted-foreground">Download them together as one .zip from the toolbar.</p>
      </aside>
    );
  }

  if (!item) {
    return (
      <aside aria-label="Details" className="py-1 text-sm text-muted-foreground">
        Select a file or folder to see its details and who has access.
      </aside>
    );
  }

  const people = access?.entries || [];

  return (
    <aside aria-label="Details" className="flex flex-col gap-5 text-[13.5px]">
      <Thumb item={item} />
      <div className="flex flex-col gap-1">
        <div className="break-words text-[15px] font-semibold leading-snug">{item.name}</div>
        <div className="text-sm text-muted-foreground">
          {kindOf(item).label}
          {item.kind !== 'folder' && `, ${formatSize(item.size)}`}
          <br />
          Edited {formatDate(item.modified_at).replace(/^Today/, 'today')}
          {item.modified_by && ` by ${item.modified_by.name}`}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">Who has access</span>
          <button type="button" onClick={() => onShare(item)} className="rounded px-1 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {item.role >= ROLES.EDITOR ? 'Manage' : 'View'}
          </button>
        </div>
        {access === null ? (
          <div className="h-[34px] w-32 animate-pulse rounded-full bg-muted" />
        ) : (
          <>
            <div className="flex items-center">
              {people.slice(0, 4).map((e, i) => (
                <span
                  key={e.id}
                  title={e.user?.name || e.team?.name}
                  className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-card text-[10.5px] font-semibold ${e.team ? 'text-white' : 'bg-ink text-ink-foreground'} ${i ? '-ml-2' : ''}`}
                  style={e.team ? { background: e.team.color } : undefined}
                >
                  {e.team ? <Users className="h-4 w-4" aria-hidden="true" /> : initials(e.user.name)}
                </span>
              ))}
              {people.length > 4 && (
                <span className="-ml-2 flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-muted text-xs font-bold">+{people.length - 4}</span>
              )}
              {people.length === 0 && access.owner && <span className="text-sm text-muted-foreground">Only you ({access.owner.name})</span>}
            </div>
            <ul className="flex flex-col gap-2 text-sm">
              {people.slice(0, 3).map((e) => (
                <li key={e.id} className="flex items-center gap-2">
                  {e.team ? <Users className="h-[15px] w-[15px] text-muted-foreground" aria-hidden="true" /> : null}
                  <span className="truncate">{e.team?.name || e.user?.name}, {e.role_label.toLowerCase()}</span>
                </li>
              ))}
              {item.restricted && (
                <li className="flex items-center gap-2 font-medium">
                  <Lock className="h-[15px] w-[15px]" aria-hidden="true" /> Restricted — parent access doesn’t apply
                </li>
              )}
            </ul>
          </>
        )}
      </div>

      {events && events.length > 0 && (
        <div className="flex flex-col gap-2.5 border-t border-border pt-4">
          <span className="text-xs font-medium text-muted-foreground">Activity</span>
          <ul className="flex flex-col gap-2.5 text-sm text-muted-foreground">
            {events.map((e) => (
              <li key={e.id}>
                <b className="text-foreground">{auditActor(e)}</b> {auditLabel(e.action, { withObject: e.node_name !== item.name })}
                {e.node_name !== item.name && ` “${e.node_name}”`}
                <span className="block text-xs">{formatDate(e.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
};

export default DetailsPanel;
